import type { Editor } from '@tiptap/react';
import type { EditorState } from '@tiptap/pm/state';
import React, { useEffect } from 'react';
import { EditorContent, NodeViewWrapper, ReactNodeViewRenderer, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { NodeSelection } from '@tiptap/pm/state';
import { TableKit } from '@tiptap/extension-table';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { Button, ButtonIcon } from '../../../../components';

const isLogoImageNode = (node: any): boolean => {
  if (!node || node.type?.name !== 'image') {
    return false;
  }

  const logoAttr = node.attrs?.dataLogo;

  return !(logoAttr == null || logoAttr === false || logoAttr === 'false');
};

const isLogoSelected = (state: EditorState): boolean => {
  const { selection } = state;

  if (selection instanceof NodeSelection) {
    return isLogoImageNode(selection.node);
  }

  let hasLogo = false;

  state.doc.nodesBetween(selection.from, selection.to, (node) => {
    if (isLogoImageNode(node)) {
      hasLogo = true;
      return false;
    }
    return true;
  });

  return hasLogo;
};

const ImageNodeView = ({ node, deleteNode }: any) => {
  const isLogo = isLogoImageNode(node);
  const dataLogo = node.attrs?.dataLogo;

  const onDelete = () => {
    if (isLogo) {
      return;
    }
    deleteNode();
  };

  return (
    <NodeViewWrapper className={'relative inline-block group w-full'}>
      <img
        src={node.attrs.src}
        alt={node.attrs.alt || ''}
        className={'max-w-full h-auto'}
        data-logo={dataLogo ?? undefined}
      />
      {!isLogo && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <ButtonIcon
            icon={'Delete'}
            onClick={onDelete}
            title={'Delete image'}
            size={'sm'}
          />
        </div>
      )}
    </NodeViewWrapper>
  );
};

const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      dataLogo: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-logo'),
        renderHTML: (attributes) => {
          if (attributes.dataLogo == null) {
            return {};
          }

          return { 'data-logo': attributes.dataLogo };
        },
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
});

const MenuBar = ({ editor }: { editor: Editor | null }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="sticky top-0 flex flex-wrap gap-2 border-b py-2 mb-2 bg-white z-10">
      <ButtonIcon icon={'FormatBold'} onClick={() => editor.chain().focus().toggleBold().run()} isOutline={!editor.isActive('bold')} title={'Bold'}/>
      <ButtonIcon icon={'FormatItalic'} onClick={() => editor.chain().focus().toggleItalic().run()} isOutline={!editor.isActive('italic')} title={'Italic'} />
      <ButtonIcon icon={'FormatUnderlined'} onClick={() => editor.chain().focus().toggleUnderline().run()} isOutline={!editor.isActive('underline')} title={'Underline'} />
      <ButtonIcon icon={'FormatStrikethrough'}  onClick={() => editor.chain().focus().toggleStrike().run()} isOutline={!editor.isActive('strike')} title={'Strikethrough'} />
      <ButtonIcon icon={'FormatAlignLeft'} onClick={() => editor.chain().focus().setTextAlign('left').run()} isOutline={!editor.isActive({ textAlign: 'left' })} title={'Align Left'} />
      <ButtonIcon icon={'FormatAlignCenter'} onClick={() => editor.chain().focus().setTextAlign('center').run()} isOutline={!editor.isActive({ textAlign: 'center' })} title={'Align Center'} />
      <ButtonIcon icon={'FormatAlignRight'} onClick={() => editor.chain().focus().setTextAlign('right').run()} isOutline={!editor.isActive({ textAlign: 'right' })} title={'Align Right'} />
    </div>
  );
};

const ImpactReportEditor = ({ content, save, send, status = 'idle' }: { content: string | null, save: (data: any) => void, send: (data: any) => void, status: 'idle' | 'saving' | 'sending' | 'saved' | 'sent' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [ 1, 2, 3 ] },
      }),
      TableKit.configure({
        table: { resizable: true },
      }),
      CustomImage.configure({
        inline: true,
        allowBase64: true,
      }),
      Underline,
      Link,
      Highlight,
      TextAlign.configure({ types: [ 'heading', 'paragraph' ] }),
    ],
    content: '<p>Start typing here...</p>',
    editorProps: {
      handleKeyDown: (view, event) => {
        if (![ 'Backspace', 'Delete' ].includes(event.key)) {
          return false;
        }

        if (!isLogoSelected(view.state)) {
          return false;
        }

        event.preventDefault();
        return true;
      },
    },
  });

  useEffect(() => {
    if (editor && content) {
      editor.commands.setContent(content);
    }
  }, [ content, editor ]);

  const saveImpactReport = () => {
    if (editor) {
      const htmlContent = editor.getHTML();
      save({ data: { content: htmlContent } });
    }
  };

  const sendImpactReport = () => {
    if (editor) {
      const htmlContent = editor.getHTML();
      send({ data: { content: htmlContent } });
    }
  };

  const saveButtonLabel = status === 'saving' ? 'Saving...' : (status === 'saved' ? 'Saved' : 'Save Edits');
  const sendButtonLabel = status === 'sending' ? 'Sending...' : (status === 'sent' ? 'Sent' : 'Send Impact Report');

  return (
    <div className={'w-full mx-auto p-4 border rounded-md shadow space-y-4'}>
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className="ProseMirror border p-2 rounded" />
      <div className={'flex justify-center gap-4'}>
        <Button
          type={'submit'}
          color={'positive'}
          className={'w-1/6'}
          isOutline
          disabled={[ 'saving' ].includes(status)}
          onClick={saveImpactReport}
        >
          {saveButtonLabel}
        </Button>
        <Button
          type={'submit'}
          color={'positive'}
          className={'w-1/4'}
          disabled={[ 'sending' ].includes(status)}
          onClick={sendImpactReport}
        >
          {sendButtonLabel}
        </Button>
      </div>
    </div>
  );
};

export { ImpactReportEditor };
